import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

type StepState = 'done' | 'current' | 'upcoming';

interface StepperStep {
  label: string;
  state: StepState;
  number: number;
}

/**
 * Purchase is always done once we are in the post-purchase funnel.
 * `currentIndex` is the index into `offerLabels` (Priority / Settlements / Register / Get App).
 */
function buildSteps(offerLabels: string[], currentIndex: number): StepperStep[] {
  const labels = ['Purchase', ...offerLabels];
  return labels.map((label, i) => {
    const offerIndex = i - 1;
    let state: StepState = 'upcoming';
    if (i === 0 || offerIndex < currentIndex) state = 'done';
    else if (offerIndex === currentIndex) state = 'current';
    return { label, state, number: i + 1 };
  });
}

function StepIndicator({ step }: { step: StepperStep }) {
  if (step.state === 'done') {
    return (
      <div className="flex size-6 items-center justify-center rounded-full bg-mf-green">
        <Check className="size-3.5 text-white" strokeWidth={3} />
      </div>
    );
  }
  if (step.state === 'current') {
    return (
      <div className="flex size-6 items-center justify-center rounded-full border-2 border-mf-green">
        <span className="size-3 rounded-full bg-mf-green" />
      </div>
    );
  }
  return (
    <div className="flex size-6 items-center justify-center rounded-full bg-mf-neutral-100">
      <span className="font-body text-[10px] font-bold leading-3 text-mf-neutral-900">{step.number}</span>
    </div>
  );
}

interface OfferStepperProps {
  offerLabels: string[];
  currentIndex: number;
  className?: string;
}

export function OfferStepper({ offerLabels, currentIndex, className }: OfferStepperProps) {
  const steps = buildSteps(offerLabels, currentIndex);

  return (
    <div className={cn('flex items-center bg-white p-2', className)}>
      {steps.map((step, i) => (
        <div key={`${step.label}-${i}`} className="flex min-w-0 flex-1 flex-col items-center gap-0.5">
          <div className="flex w-full items-center gap-1">
            <span className={cn('h-px flex-1', i === 0 ? 'bg-transparent' : 'bg-mf-neutral-200')} />
            <StepIndicator step={step} />
            <span
              className={cn(
                'h-px flex-1',
                i === steps.length - 1 ? 'bg-transparent' : 'bg-mf-neutral-200',
              )}
            />
          </div>
          <span
            className={cn(
              'w-full text-center font-body text-[10px] font-medium leading-3',
              step.state === 'upcoming' ? 'text-mf-neutral-400' : 'text-mf-neutral-900',
            )}
          >
            {step.label}
          </span>
        </div>
      ))}
    </div>
  );
}
